import React, { useState } from 'react';
import { Check, Crown, FileText, Calendar, Percent, BookOpen, Sparkles, PauseCircle, RefreshCw, ArrowRight, AlertTriangle, X, Clock, TrendingUp, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
interface SubscriptionsTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
export function SubscriptionsTab({
  theme,
  onNavigate
}: SubscriptionsTabProps) {
  const {
    showToast
  } = useToast();
  const [hasSubscription, setHasSubscription] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  // Mock subscription data
  const sub = {
    plan: 'Astriqo Plus',
    price: 990,
    nextBilling: '12 Ноя 2024',
    daysLeft: 12,
    startDate: '12 Окт 2024',
    reportsUsed: 2,
    reportsTotal: 3,
    reportsRemaining: 1,
    savingsAmount: 2400,
    consultationsDiscount: 20,
    discountUsedTimes: 3,
    discountSaved: 1500
  };
  const benefits = [{
    icon: <FileText className="w-4 h-4" data-id="element-19341" />,
    label: '3 отчёта/мес',
    desc: 'Бесплатные авто-отчёты',
    bg: 'bg-indigo-50',
    text: 'text-indigo-600'
  }, {
    icon: <Percent className="w-4 h-4" data-id="element-19342" />,
    label: '−20% консультации',
    desc: 'Скидка на астрологов',
    bg: 'bg-emerald-50',
    text: 'text-emerald-600'
  }, {
    icon: <BookOpen className="w-4 h-4" data-id="element-19343" />,
    label: 'PRO статьи',
    desc: 'Закрытый контент',
    bg: 'bg-amber-50',
    text: 'text-amber-600'
  }, {
    icon: <Calendar className="w-4 h-4" data-id="element-19344" />,
    label: 'PRO календарь',
    desc: 'Персональные транзиты',
    bg: 'bg-purple-50',
    text: 'text-purple-600'
  }];
  const plans = [{
    name: 'Бесплатно',
    price: 0,
    features: ['Базовый календарь', 'Общие прогнозы', '1 профиль'],
    current: false
  }, {
    name: 'Astriqo Plus',
    price: 990,
    features: ['3 отчёта/мес', '−20% консультации', 'PRO календарь', 'Закрытые статьи', '5 профилей'],
    current: false,
    popular: true
  }, {
    name: 'Astriqo Pro',
    price: 1990,
    features: ['10 отчётов/мес', '−30% консультации', 'Приоритет поддержки', 'Все PRO функции', '∞ профилей'],
    current: false
  }];
  // No subscription state
  if (!hasSubscription) {
    return <div className="space-y-5" data-id="element-19345">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-19346">
          <div data-id="element-19347">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19348">
              Подписка
            </h2>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-19349">
              Выберите тариф для максимальной пользы
            </p>
          </div>
        </div>

        {/* Social proof */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-19350">
          <TrendingUp className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-19351" />
          <p className="text-xs text-indigo-800" data-id="element-19352">
            <span className="font-bold" data-id="element-19353">87% подписчиков</span> экономят от 2 000
            до 5 000 ₽ ежемесячно на отчётах и консультациях
          </p>
        </div>

        {/* Plan comparison */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-19354">
          {plans.map(plan => <div key={plan.name} className={`bg-white border rounded-xl p-4 relative ${plan.popular ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-slate-200'}`} data-id="element-19355">
              {plan.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[9px] font-bold px-2.5 py-0.5 rounded-full" data-id="element-19356">
                  Популярный
                </span>}
              <h4 className="font-bold text-slate-900 text-sm mb-1" data-id="element-19357">
                {plan.name}
              </h4>
              <div className="mb-3" data-id="element-19358">
                {plan.price > 0 ? <span className="text-2xl font-bold text-slate-900" data-id="element-19359">
                    {plan.price} ₽
                    <span className="text-xs font-normal text-slate-500" data-id="element-19360">
                      /мес
                    </span>
                  </span> : <span className="text-2xl font-bold text-slate-900" data-id="element-19361">0 ₽</span>}
              </div>
              <ul className="space-y-1.5 mb-4" data-id="element-19362">
                {plan.features.map((f, i) => <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-19363">
                    <Check className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-19364" /> {f}
                  </li>)}
              </ul>
              <Button variant={plan.popular ? 'primary' : 'outline'} size="sm" className="w-full text-xs" onClick={() => {
            setHasSubscription(true);
            showToast(`Подписка ${plan.name} активирована!`);
          }} data-id="element-19365">
                {plan.price === 0 ? 'Текущий' : 'Подключить'}
              </Button>
            </div>)}
        </div>
      </div>;
  }
  return <div className="space-y-5" data-id="element-19366">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-19367">
        <div data-id="element-19368">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19369">
            Моя подписка
          </h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-19370">
            Управление и статистика
          </p>
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm relative overflow-hidden" data-id="element-19371">
        <div className="absolute top-0 right-0 opacity-5" data-id="element-19372">
          <Crown className="w-28 h-28 -mt-4 -mr-4" data-id="element-19373" />
        </div>
        <div className="relative z-10" data-id="element-19374">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4" data-id="element-19375">
            <div data-id="element-19376">
              <div className="flex items-center gap-2 mb-1" data-id="element-19377">
                <h3 className="text-xl font-bold text-slate-900" data-id="element-19378">{sub.plan}</h3>
                {isPaused ? <Badge variant="warning" size="sm" className="text-[10px]" data-id="element-19379">
                    На паузе
                  </Badge> : <Badge variant="success" size="sm" className="text-[10px]" data-id="element-19380">
                    Активна
                  </Badge>}
              </div>
              <p className="text-slate-500 text-xs" data-id="element-19381">Ежемесячная подписка</p>
            </div>
            <div className="text-right" data-id="element-19382">
              <div className="text-3xl font-bold text-slate-900" data-id="element-19383">
                {sub.price} ₽
                <span className="text-sm font-normal text-slate-400" data-id="element-19384">
                  {' '}
                  /мес
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6" data-id="element-19385">
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2" data-id="element-19386">
              <Clock className="w-4 h-4 text-slate-400" data-id="element-19387" />
              <div data-id="element-19388">
                <div className="text-[10px] text-slate-500" data-id="element-19389">
                  Следующее списание
                </div>
                <div className="text-xs font-bold text-slate-900" data-id="element-19390">
                  {sub.nextBilling}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2" data-id="element-19391">
              <Calendar className="w-4 h-4 text-slate-400" data-id="element-19392" />
              <div data-id="element-19393">
                <div className="text-[10px] text-slate-500" data-id="element-19394">Осталось</div>
                <div className="text-xs font-bold text-slate-900" data-id="element-19395">
                  {sub.daysLeft} дней
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Stats — "Вы уже получили" */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-19396">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2" data-id="element-19397">
          <Sparkles className="w-4 h-4 text-indigo-600" data-id="element-19398" />
          <span className="text-sm font-bold text-slate-900" data-id="element-19399">
            Вы уже получили
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100" data-id="element-19400">
          <div className="p-4 text-center" data-id="element-19401">
            <div className="text-2xl font-bold text-indigo-600" data-id="element-19402">
              {sub.reportsUsed}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19403">
              Отчётов использовано
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-19404">
            <div className="text-2xl font-bold text-emerald-600" data-id="element-19405">
              {sub.reportsRemaining}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19406">
              Осталось в этом месяце
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-19407">
            <div className="text-2xl font-bold text-amber-600" data-id="element-19408">
              {sub.savingsAmount.toLocaleString('ru-RU')} ₽
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19409">
              Сэкономлено на отчётах
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-19410">
            <div className="text-2xl font-bold text-purple-600" data-id="element-19411">
              {sub.discountSaved.toLocaleString('ru-RU')} ₽
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19412">
              Сэкономлено на скидках
            </div>
          </div>
        </div>
      </div>

      {/* Reports remaining bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-19413">
        <div className="flex items-center justify-between mb-2" data-id="element-19414">
          <span className="text-xs font-bold text-slate-900" data-id="element-19415">
            Бесплатные отчёты в этом месяце
          </span>
          <span className="text-xs text-slate-500" data-id="element-19416">
            {sub.reportsUsed} из {sub.reportsTotal} использовано
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2.5" data-id="element-19417">
          <div className="bg-indigo-500 h-2.5 rounded-full transition-all" style={{
          width: `${sub.reportsUsed / sub.reportsTotal * 100}%`
        }} data-id="element-19418" />
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5" data-id="element-19419">
          Осталось{' '}
          <span className="font-bold text-indigo-600" data-id="element-19420">
            {sub.reportsRemaining}
          </span>{' '}
          бесплатных отчётов. Обновление 12 Ноя 2024.
        </p>
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-19421">
        {benefits.map((b, i) => <div key={i} className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-sm" data-id="element-19422">
            <div className={`w-9 h-9 rounded-lg ${b.bg} flex items-center justify-center mx-auto mb-2 ${b.text}`} data-id="element-19423">
              {b.icon}
            </div>
            <div className="text-xs font-bold text-slate-900" data-id="element-19424">{b.label}</div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19425">{b.desc}</div>
          </div>)}
      </div>

      {/* Actions */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3" data-id="element-19426">
        <div className="flex items-center justify-between" data-id="element-19427">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-19428">Управление</h4>
          <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-19429">
            Скоро
          </Badge>
        </div>
        <p className="text-xs text-slate-500" data-id="element-19430">
          Управление подпиской будет доступно в ближайшем обновлении.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-19431">
          <Button variant="primary" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-19432">
            <ArrowRight className="w-3.5 h-3.5 mr-1" data-id="element-19433" /> Сменить тариф
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-19434">
            <RefreshCw className="w-3.5 h-3.5 mr-1" data-id="element-19435" /> Продлить заранее
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-19436">
            <PauseCircle className="w-3.5 h-3.5 mr-1" data-id="element-19437" /> Приостановить
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-19438">
            <X className="w-3.5 h-3.5 mr-1" data-id="element-19439" /> Отменить подписку
          </Button>
        </div>
      </div>

      {/* Cancel Warning */}
      {showCancelWarning && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-3" data-id="element-19440">
          <div className="flex items-start gap-3" data-id="element-19441">
            <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-19442" />
            <div data-id="element-19443">
              <h4 className="font-bold text-rose-900 text-sm" data-id="element-19444">Вы уверены?</h4>
              <p className="text-xs text-rose-700 mt-1 leading-relaxed" data-id="element-19445">
                При отмене подписки вы потеряете:
              </p>
              <ul className="mt-2 space-y-1" data-id="element-19446">
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-19447">
                  <X className="w-3 h-3 shrink-0" data-id="element-19448" /> {sub.reportsRemaining}{' '}
                  неиспользованных отчётов
                </li>
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-19449">
                  <X className="w-3 h-3 shrink-0" data-id="element-19450" /> Скидку 20% на консультации
                </li>
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-19451">
                  <X className="w-3 h-3 shrink-0" data-id="element-19452" /> PRO календарь и закрытые
                  статьи
                </li>
              </ul>
              <p className="text-[10px] text-rose-600 mt-2" data-id="element-19453">
                За время подписки вы сэкономили{' '}
                <span className="font-bold" data-id="element-19454">
                  {(sub.savingsAmount + sub.discountSaved).toLocaleString('ru-RU')}{' '}
                  ₽
                </span>
              </p>
            </div>
          </div>
          <div className="flex gap-2 pt-1" data-id="element-19455">
            <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setShowCancelWarning(false)} data-id="element-19456">
              Оставить подписку
            </Button>
            <Button variant="outline" size="sm" className="flex-1 text-xs text-rose-600 border-rose-300 hover:bg-rose-100" onClick={() => {
          setHasSubscription(false);
          setShowCancelWarning(false);
          showToast('Подписка отменена. Доступ сохранится до 12 Ноя 2024');
        }} data-id="element-19457">
              Всё равно отменить
            </Button>
          </div>
        </div>}

      {/* Savings highlight */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-19458">
        <Shield className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-19459" />
        <p className="text-xs text-emerald-800" data-id="element-19460">
          С подпиской вы уже сэкономили{' '}
          <span className="font-bold" data-id="element-19461">
            {(sub.savingsAmount + sub.discountSaved).toLocaleString('ru-RU')} ₽
          </span>
          . Обычно подписчики экономят 20–30% на услугах платформы.
        </p>
      </div>
    </div>;
}